import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import IconButton from '@mui/material/IconButton';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from '@mui/material';
import api from '../../api/axios';

interface Brand {
  _id: string;
  name: string;
  iconKey?: string | null;
}

interface Model {
  _id: string;
  name: string;
  brandId: string;
  order?: number;
}


const BrandPage = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();

  const [brand, setBrand] = useState<Brand | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [editedNames, setEditedNames] = useState<Record<string, string>>({});
  const [savingIds, setSavingIds] = useState<string[]>([]);
  const [newModel, setNewModel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!brandId) return;

    const fetchBrandAndModels = async () => {
      setLoading(true);
      try {
        const [brandRes, modelsRes] = await Promise.all([
          api.get<Brand>(`/api/admin/brands-car/${brandId}`),
          api.get<Model[]>(`/api/admin/by-brand/${brandId}`),
        ]);
        setBrand(brandRes.data);
        setModels(modelsRes.data);

        const initialNames: Record<string, string> = {};
        modelsRes.data.forEach((m) => (initialNames[m._id] = m.name));
        setEditedNames(initialNames);
      } catch (err) {
        setError('Ошибка при загрузке данных.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBrandAndModels();
  }, [brandId]);

  const addModel = async () => {
    if (!newModel.trim()) return;

    try {
      const response = await api.post<Model>('/api/admin/models', {
        name: newModel.trim(),
        brandId,
      });
      setModels((prev) => [...prev, response.data]);
      setEditedNames((prev) => ({ ...prev, [response.data._id]: response.data.name }));
      setNewModel('');
    } catch (err) {
      setError('Ошибка при добавлении модели.');
      console.error(err);
    }
  };

  const deleteModel = async (modelId: string) => {
    try {
      await api.delete(`/api/admin/models/${modelId}`);
      setModels((prev) => prev.filter((model) => model._id !== modelId));
    } catch (err) {
      setError('Ошибка при удалении модели.');
      console.error(err);
    }
  };

  const saveModel = async (modelId: string) => {
    const newName = editedNames[modelId];
    if (!newName.trim()) return;

    setSavingIds((prev) => [...prev, modelId]);
    try {
      const response = await api.put<Model>(`/api/admin/models/${modelId}`, {
        name: newName.trim(),
      });
      setModels((prev) =>
        prev.map((m) => (m._id === modelId ? { ...m, name: response.data.name } : m))
      );
    } catch (err) {
      setError('Ошибка при сохранении модели.');
      console.error(err);
    } finally {
      setSavingIds((prev) => prev.filter((id) => id !== modelId));
    }
  };

  const moveModel = async (modelId: string, direction: 'up' | 'down') => {
    if (!models.length) return;

    const currentIndex = models.findIndex(m => m._id === modelId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= models.length) return;

    const reordered = [...models];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(newIndex, 0, moved);

    const withOrder = reordered.map((m, index) => ({
      ...m,
      order: index,
    }));

    setModels(withOrder);

    try {
      await Promise.all(
        withOrder.map(m =>
          api.put(`/api/admin/models/${m._id}`, {
            order: m.order,
          }),
        ),
      );
    } catch (err) {
      console.error(err);
      setError('Ошибка при изменении порядка моделей.');
    }
  };



  return (
    <Box p={4}>
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        ← Назад
      </Button>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : brand ? (
        <>
          <Typography variant="h5" gutterBottom>
            Марка автомобиля: {brand.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            ID: {brand._id}
          </Typography>

          <Box display="flex" gap={2} mb={3}>
            <TextField
              label="Новая модель"
              value={newModel}
              onChange={(e) => setNewModel(e.target.value)}
            />
            <Button variant="contained" onClick={addModel}>
              Добавить модель
            </Button>
          </Box>

          {models.length === 0 ? (
            <Typography>Моделей пока нет.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Название модели</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {models.map((model, index) => (
                    <TableRow key={model._id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={editedNames[model._id] ?? ''}
                          onChange={(e) =>
                            setEditedNames((prev) => ({
                              ...prev,
                              [model._id]: e.target.value,
                            }))
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => moveModel(model._id, 'up')}
                          disabled={index === 0}
                          aria-label="Выше"
                        >
                          <ArrowUpwardIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveModel(model._id, 'down')}
                          disabled={index === models.length - 1}
                          aria-label="Ниже"
                        >
                          <ArrowDownwardIcon fontSize="inherit" />
                        </IconButton>

                        <IconButton
                          color="primary"
                          onClick={() => saveModel(model._id)}
                          aria-label="Сохранить"
                          disabled={savingIds.includes(model._id)}
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => deleteModel(model._id)}
                          aria-label="Удалить"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <Typography>Марка не найдена.</Typography>
      )}
    </Box>
  );
};

export default BrandPage;
